
import * as React from 'react';
import {
  ISimpleComponentDecorator,
  ISimpleDecoratedComponentType,
  ISimpleDecoratedProps
} from '../../utilities/components/Decorators';
import { IItemCacheDispatch } from '../../store/actions/ItemCacheActions';
import { IItemCacheDependencies, IItemCacheDemands } from '../../models/itemCache/ItemCacheObserver';
import { removeItemDemands, registerItemDemands, recordLastItemAccesses } from '../../operations/pruneItems';
import { batchedGetItems } from '../../operations/getItems';
import { IControlFaceted } from './withControlHandler';

export interface IItemCacheDemanderProps {
  itemCacheDemanderDispatch: IItemCacheDispatch;
  itemCacheDemanderDependencies: IItemCacheDependencies;
  itemCacheDemanderDemands: IItemCacheDemands;
  itemCacheConnectorControlHandlers: IControlFaceted;
}

export function withItemCacheDemander(): ISimpleComponentDecorator<IItemCacheDemanderProps> {
  return <P extends {}>(
    component: React.ComponentType<P>
  ): ISimpleDecoratedComponentType<P, IItemCacheDemanderProps> => {
    // If the wrapped component is Fragment, use a renderer which avoids passing any props beyond 'children'
    // to the Fragment.
    const render = component === React.Fragment ? renderFragmentWithProps : renderComponentWithProps;

    type IProps = ISimpleDecoratedProps<P, IItemCacheDemanderProps>;

    /**
     * Wrapper component whcih makes getItems calls if the mapItemCacheToProps function
     * provided for the wrapped component updates its demands or its dependencies change.
     */
    class ItemCacheDemander extends React.PureComponent<IProps> {
      private _demandOwner: {};

      constructor(props: IProps) {
        super(props);

        this._demandOwner = {};
      }

      public render(): JSX.Element | null {
        return render(component, this.props as {} as IItemCacheDemanderProps);
      }

      public componentDidMount(): void {
        this._getItems();
      }

      public componentDidUpdate(previousProps: Readonly<IItemCacheDemanderProps>): void {
        const {
          itemCacheDemanderDemands,
          itemCacheDemanderDependencies
        } = this.props;

        if (itemCacheDemanderDemands !== previousProps.itemCacheDemanderDemands ||
          itemCacheDemanderDependencies !== previousProps.itemCacheDemanderDependencies) {
          // If the demands or dependencies have changed, issue a getItems request to the store.
          // The getItems action will internally de-dupe, so it is safe to issue this request
          // multiple times.

          // TODO Instead of invoking getItems right here, roll the demands up to the 'parent'
          // ItemCacheDemander via context. If only the root wrappers make requests, there
          // could be a lot less churn on the operation and data source layers.
          this._getItems();
        }
      }

      public componentWillUnmount(): void {
        this.props.itemCacheDemanderDispatch(removeItemDemands({
          owner: this._demandOwner
        }));
      }

      private _getItems(): void {
        const {
          itemCacheDemanderDispatch,
          itemCacheDemanderDemands,
          itemCacheDemanderDependencies
        } = this.props;

        itemCacheDemanderDispatch(recordLastItemAccesses({
          items: itemCacheDemanderDependencies
        }));

        itemCacheDemanderDispatch(batchedGetItems({
          items: itemCacheDemanderDemands
        }));

        itemCacheDemanderDispatch(registerItemDemands({
          owner: this._demandOwner,
          // Register all items touched in the selector callback,
          // regardless of whether a demand is issued.
          items: itemCacheDemanderDependencies
        }));
      }
    }

    return ItemCacheDemander;
  };
}

function renderComponentWithProps<P>(
  Component: React.ComponentType<P>,
  props: IItemCacheDemanderProps & Pick<React.Props<{}>, 'children'>
): JSX.Element | null {
  const {
    itemCacheDemanderDispatch,
    itemCacheDemanderDemands,
    itemCacheDemanderDependencies,
    itemCacheConnectorControlHandlers,
    // Forward the rest of the props to the wrapped component.
    ...originalProps
  } = props as IItemCacheDemanderProps;

  return (
    <Component { ...originalProps } />
  );
}

function renderFragmentWithProps<P>(
  Component: React.ComponentType<P>,
  props: IItemCacheDemanderProps & Pick<React.Props<{}>, 'children'>
): JSX.Element | null {
  return <>{ props.children }</>;
}
